import { mapActions, mapGetters } from 'vuex'

import { initiate, subscribe } from '@/services/pusher'
import { EventBus } from '@/services/eventbus'

import privilegesMixin from '@/mixins/common/privilegesMixin'
import { returnJson } from '@/helpers/api'
import statusMixin from '@/mixins/common/statusMixin'
import { updateChargingPoint } from '@/../shared/API/chargingpoints'

export default {
  mixins: [privilegesMixin, statusMixin],
  data() {
    return {
      channel: null,
      isDbOccupied: false,
    }
  },
  computed: {
    ...mapGetters('access', [
      'getActiveMunicipality',
      'canAccessLayer',
    ]),
    ...mapGetters('users', [
      'getUsersByCode',
    ]),
  },
  methods: {
    ...mapActions('planmode', [
      'addOrUpdateChargingPoint',
      'deselectChargingPoint',
      'clearAlternativeLocationForUuid',
      'getStakeholderByUUID',
    ]),
    $_chargingpointEditMixin_updateStakeholders: async function ({ stakeholderUUID, stakeholderType }) {
      if (!stakeholderUUID) return null

      const existingStakeholder = this.stakeholders?.find(s => s.category === stakeholderType)

      if (!existingStakeholder || existingStakeholder.uuid !== stakeholderUUID) {
        try {
          const stakeholder = await this.getStakeholderByUUID({ uuid: stakeholderUUID })

          return {
            name: stakeholder.name,
            category: stakeholderType,
            uuid: stakeholder.uuid,
          }
        } catch (err) {
          console.error('Error fetching stakeholder:', err)
          return null
        }
      } else if (existingStakeholder.uuid === stakeholderUUID) {
        return existingStakeholder
      }

      return null
    },
    $_chargingpointEditMixin_connectToPusher: async function () {
      const token = await this.$auth.getTokenSilently()
      initiate({ token })
      let { channel } = await subscribe({ channel: this.getActiveMunicipality })
      this.channel = channel
    },
    $_chargingpointEditMixin_exists: async function ({ code, refId }) {
      const token = await this.$auth.getTokenSilently()
      const api = await fetch('/api/chargingpointexists', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ code, refId }), // '1930'
      })

      return await api.json()
    },
    /**
     * Persists a charging point update via APIv3 PUT /chargingpoint/{uuid}.
     *
     * Accepts { uuid, ...diff } — only the fields that changed. Callers are
     * responsible for computing the diff; this method knows nothing about the
     * document shape.
     *
     * APIv3 returns { data: chargingpoint }.
     * Normalized here so Vuex callers are unaffected.
     */
    $_chargingpointEditMixin_save: async function ({ data, rollback }) {
      const { uuid, code, ...payload } = data

      if (!Object.keys(payload).length) {
        // Nothing changed — skip the round trip entirely
        return
      }

      const response = await updateChargingPoint({ chargingpointUuid: uuid, payload })
        .catch((err) => {
          console.log(err)
          this.$notify({
            type: 'error',
            title: 'Niet gelukt!',
            text: 'Er ging iets mis tijdens het opslaan van de locatie.',
          })
          this.addOrUpdateChargingPoint({ chargingpoint: rollback })
        })

      const chargingpoint = response?.data
      if (chargingpoint) {
        // APIv3 does not return code on the document — merge it back so
        // addOrUpdateChargingPoint can match against the active municipality.
        const normalized = { ...chargingpoint, code }
        this.addOrUpdateChargingPoint({ chargingpoint: normalized })

        if (this.channel) {
          this.channel.trigger('client-chargingpoint-saved', { chargingpoint: normalized })
        }
      }
    },

    $_chargingpointEditMixin_delete: async function ({ code, _id }) {
      this.isDbOccupied = true

      const token = await this.$auth.getTokenSilently()
      const response = await fetch('/api/chargingpointsdelete', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ code, _id }), // '1930'
      })
        // .then(await checkStatus)
        .then(returnJson)
        .catch(() => this.$notify({
          type: 'error',
          title: 'Niet gelukt!',
          text: 'Er ging iets mis tijdens het verwijderen van de locatie.',
        }))

      if (response.chargingpoint) {
        this.addOrUpdateChargingPoint({
          chargingpoint: response.chargingpoint,
        })

        if (this.channel) {
          this.channel.trigger('client-chargingpoint-saved', {
            chargingpoint: response.chargingpoint,
          })
        }
      }

      this.isDbOccupied = false

      // broadcast to close popup / sidebar
      EventBus.$emit('deselect-chargingpoint')
    },
  },
}